/**
 * Titan Electric — Auth helper
 *
 * Owns the "remembered session" behavior: store a token + user locally
 * once login succeeds, validate it against the server on app open, and
 * clear it on logout. Login.js (and anything else that needs auth) should
 * go through these functions rather than touching localStorage directly.
 */

const API_BASE    = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
const STORAGE_KEY = 'titan_session';

function getStoredSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setStoredSession(session) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function clearStoredSession() {
  localStorage.removeItem(STORAGE_KEY);
}

async function apiFetch(path, options = {}) {
  const session = getStoredSession();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (session?.token) headers['x-worker-token'] = session.token;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// List of active users for the login picker. No auth required for this
// one — it's shown before login.
export async function fetchUserPicker() {
  const res = await fetch(`${API_BASE}/users/picker`);
  if (!res.ok) throw new Error('Could not load user list');
  return res.json();
}

// Worker login — userId comes from picking a name in fetchUserPicker().
export async function loginWorker(userId, pin) {
  const data = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ userId, pin }),
  });
  setStoredSession({ token: data.token, user: data.user, role: data.user.role });
  return data.user;
}

// Staff login (OfficeStaff, Accounting, ProjectManager) — username/password.
export async function loginStaff(username, password) {
  const data = await apiFetch('/auth/staff', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  setStoredSession({ token: data.token, user: data.user, role: data.user.role });
  return data.user;
}

// Admin login — username/password.
export async function loginAdmin(username, password) {
  const data = await apiFetch('/auth/admin', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  setStoredSession({ token: data.token, user: { id: 'admin', role: 'admin' }, role: 'admin' });
  return { id: 'admin', role: 'admin' };
}

// Call on app open. Resolves to the current user if the stored token is
// still valid, or null if there's no session / it's been invalidated —
// in either "null" case the caller should show the login picker.
export async function checkSession() {
  const session = getStoredSession();
  if (!session?.token) return null;

  try {
    const user = await apiFetch('/me');
    return user;
  } catch {
    clearStoredSession();
    return null;
  }
}

export function logout() {
  // Kill the session on the server too, so the token is dead everywhere — not just
  // forgotten by this phone. Best effort: the local sign-out happens regardless.
  const s = getStoredSession();
  if (s?.token) {
    fetch(`${API_BASE}/auth/logout`, { method: 'POST', headers: { 'x-worker-token': s.token }, keepalive: true }).catch(() => {});
  }
  clearStoredSession();
}

export function getCurrentSession() {
  return getStoredSession();
}
